import { useAppSelector } from 'hooks/use-app-selector'
import { useEffect, useMemo, useState } from 'react'
import { Strokes } from 'store/slices/pg-slice/interface'
import { selectAiSubscriptionLoading, selectIsAiSubscriptionActive } from 'store/slices/subscription/subscription'
import { getSessionWeaknesses } from 'utils/weakness/get-session-weaknesses'

import { UpsellPrompt } from '../components/upsell-prompts'

// Storage key for the popup's last closed timestamp
const POPUP_LAST_CLOSED_KEY = 'post_test_upsell_popup_last_closed'
// How long to wait before showing popup again (24 hours in milliseconds)
const POPUP_HIDE_DURATION = 24 * 60 * 60 * 1000

interface PostTestUpsellProps {
  sessionData: {
    strokes: Strokes
    text: string
    wpm: number
  }
}

/**
 * Post-test upsell prompt component - automatically shows popup with user's typing weaknesses
 *
 * Calculates weaknesses from the current typing session for all users
 */
export const PostTestUpsell = ({ sessionData }: PostTestUpsellProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const isAiSubscriptionLoading = useAppSelector(selectAiSubscriptionLoading)
  const isSubscriber = useAppSelector(selectIsAiSubscriptionActive)

  /**
   * Session-based weakness calculation for all users
   * Analyzes the just-completed typing test to show personalized weaknesses
   */
  const sessionWeaknesses = useMemo(() => {
    return getSessionWeaknesses({
      strokes: sessionData.strokes,
      text: sessionData.text,
    })
  }, [sessionData])

  // Check if popup should be hidden based on last close time
  const shouldShowPopup = () => {
    if (typeof window === 'undefined') return false // Server-side rendering check

    const lastClosedTime = localStorage.getItem(POPUP_LAST_CLOSED_KEY)
    if (!lastClosedTime) return true

    const now = new Date().getTime()
    const timeSinceClosed = now - parseInt(lastClosedTime, 10)

    return timeSinceClosed > POPUP_HIDE_DURATION
  }

  // Handle popup visibility based on subscription status
  useEffect(() => {
    // Wait for subscription loading to complete before making decisions
    if (isAiSubscriptionLoading) return

    // If user is a subscriber, hide the popup
    if (isSubscriber) {
      setIsVisible(false)
      return
    }

    // If user is not a subscriber and popup should show, show it after 2 seconds
    if (shouldShowPopup()) {
      const timeout = setTimeout(() => {
        setIsVisible(true)
      }, 2000)

      return () => clearTimeout(timeout)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriber, isAiSubscriptionLoading])

  // Add/remove body class when modal is visible to hide tooltips
  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('upsell-modal-open')
    }
    return () => document.body.classList.remove('upsell-modal-open')
  }, [isVisible])

  const handleClose = () => {
    setIsVisible(false)

    // Store the current timestamp when popup is closed
    if (typeof window !== 'undefined') {
      localStorage.setItem(POPUP_LAST_CLOSED_KEY, new Date().getTime().toString())
    }
  }

  // Don't render if popup shouldn't be visible or if still loading
  if (!isVisible || isAiSubscriptionLoading) return null

  return (
    <UpsellPrompt
      title="Want Detailed Analysis & No Ads?"
      description="Upgrade to get comprehensive typing insights and an ad-free experience to focus on improving your skills."
      onClose={handleClose}
      showAskMeLater={true}
      weaknessProfile={sessionWeaknesses}
      recentWpm={sessionData.wpm}
    />
  )
}
